import { onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import type { Chart } from 'chart.js'

import { loadChartJs, type ChartJs } from './chartJsLoader'

/**
 * The whole lifecycle of a chart in `@/shared/ui`: loading Chart.js, destroying the old
 * chart, and the teardown race an await across a render opens up. A wrapper writes `draw`
 * and calls `render` from a `flush: 'post'` watch over its data — the canvas has to exist
 * before anything is drawn into it.
 *
 * `chart` is the instance drawn last, for a wrapper that has to reach it after the draw — a
 * zoom, say. It is `null` while nothing is drawn, and replaced on every render.
 *
 * Bind `element` to a `canvas`, not a `div`. Chart.js sizes the canvas from its parent, so
 * the parent carries the height, and needs `position: relative` for `responsive` to track
 * it.
 */
export function useChartJs(config: {
  /** Checked **before** the loader runs — an empty chart must not fetch the library. */
  hasData: () => boolean
  draw: (lib: ChartJs, host: HTMLCanvasElement) => Chart
}) {
  const element = ref<HTMLCanvasElement | null>(null)
  const chart = shallowRef<Chart | null>(null)

  /** Bumped on every teardown, so a render still awaiting the loader can tell it lost. */
  let generation = 0

  function destroyChart() {
    generation += 1

    chart.value?.destroy()
    chart.value = null
  }

  async function render() {
    destroyChart()

    const run = generation
    const host = element.value

    if (!host || !config.hasData()) return

    let lib

    try {
      lib = await loadChartJs()
    } catch {
      return
    }

    // Two renders over an unchanged host both pass a host check; only the counter tells
    // the older one it was superseded while it waited.
    if (run !== generation || element.value !== host) return

    chart.value = config.draw(lib, host)
  }

  watch(element, render, { flush: 'post' })

  onBeforeUnmount(destroyChart)

  return { element, chart, render }
}
